"use client";

import {
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  INPUT_MONO,
  SELECT,
} from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-mechanics/format";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  type Leg,
  type LegInput,
  solveTrip,
  type TripError,
  toHms,
} from "@/lib/tool-kit/physics-mechanics/speed-distance-time";
import {
  fromBase,
  LENGTH,
  SPEED,
  TIME,
  toBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

/** One editable leg: a distance, and either its speed or its time. */
export interface TripRow {
  id: number;
  distance: string;
  distanceUnit: string;
  kind: "speed" | "time";
  value: string;
  speedUnit: string;
  timeUnit: string;
}

export interface TripOutputUnits {
  distance: string;
  speed: string;
  time: string;
}

export interface TripStrings {
  legLabel: string;
  legDistance: string;
  legKind: string;
  legValueCaption: string;
  kindSpeed: string;
  kindTime: string;
  legValueSpeed: string;
  legValueTime: string;
  unitFor: string;
  addLeg: string;
  removeLeg: string;
  showDistanceIn: string;
  showSpeedIn: string;
  showTimeIn: string;
  tripHeading: string;
  columnLeg: string;
  columnDistance: string;
  columnSpeed: string;
  columnTime: string;
  columnTotal: string;
  totalDistance: string;
  totalTime: string;
  averageSpeed: string;
  hmsLine: string;
  hoursShort: string;
  minutesShort: string;
  secondsShort: string;
  meanNote: string;
  tripFormula: string;
  tripUnreadable: string;
  tripErrors: Record<TripError, string>;
}

const MAX_LEGS = 8;

function readRows(rows: readonly TripRow[]): LegInput[] | number {
  const inputs: LegInput[] = [];
  for (const [index, row] of rows.entries()) {
    const distance =
      row.distance.trim() === "" ? null : parseQuantity(row.distance);
    const value = row.value.trim() === "" ? null : parseQuantity(row.value);
    if (distance === null || value === null) {
      return index;
    }
    inputs.push({
      distance: toBase(distance, LENGTH, row.distanceUnit),
      kind: row.kind,
      value: toBase(
        value,
        row.kind === "speed" ? SPEED : TIME,
        row.kind === "speed" ? row.speedUnit : row.timeUnit
      ),
    });
  }
  return inputs;
}

export function hmsText(
  seconds: number,
  strings: TripStrings,
  fmt: NumberFormatter
): string {
  const { hours, minutes, seconds: rest } = toHms(seconds);
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${fmt(hours)} ${strings.hoursShort}`);
  }
  // Zero parts are left out ("2 h 30 min", not "2 h 30 min 0 s"), except a zero between
  // two non-zero ones ("1 h 0 min 5 s").
  if (minutes > 0 || (hours > 0 && rest > 0)) {
    parts.push(`${fmt(minutes)} ${strings.minutesShort}`);
  }
  if (rest > 0 || parts.length === 0) {
    parts.push(
      `${fmt(rest, { maximumFractionDigits: 2 })} ${strings.secondsShort}`
    );
  }
  return parts.join(" ");
}

function LegEditor({
  row,
  index,
  strings,
  canRemove,
  onChange,
  onRemove,
}: {
  row: TripRow;
  index: number;
  strings: TripStrings;
  canRemove: boolean;
  onChange: (patch: Partial<TripRow>) => void;
  onRemove: () => void;
}) {
  const n = index + 1;
  const base = `sdt-leg-${row.id}`;
  const valueLabel =
    row.kind === "speed" ? strings.legValueSpeed : strings.legValueTime;
  return (
    <fieldset className="grid gap-3 rounded-lg border border-border bg-card p-3 sm:grid-cols-2">
      <legend className={`${HINT} px-1`}>{t(strings.legLabel, { n })}</legend>
      <div className="space-y-1">
        <span aria-hidden="true" className={HINT}>
          {strings.columnDistance}
        </span>
        <div className="flex gap-2">
          <input
            aria-label={t(strings.legDistance, { n })}
            className={`${INPUT_MONO} min-w-0 flex-1`}
            id={`${base}-distance`}
            inputMode="decimal"
            onChange={(event) => onChange({ distance: event.target.value })}
            value={row.distance}
          />
          <select
            aria-label={t(strings.unitFor, {
              name: t(strings.legDistance, { n }),
            })}
            className={`${SELECT.replace("w-full", "")} w-24 shrink-0`}
            onChange={(event) => onChange({ distanceUnit: event.target.value })}
            value={row.distanceUnit}
          >
            {unitOptions(LENGTH).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="space-y-1">
        <span aria-hidden="true" className={HINT}>
          {strings.legValueCaption}
        </span>
        <div className="flex gap-2">
          <select
            aria-label={t(strings.legKind, { n })}
            className={`${SELECT.replace("w-full", "")} w-24 shrink-0`}
            id={`${base}-kind`}
            onChange={(event) =>
              onChange({
                kind: event.target.value === "time" ? "time" : "speed",
              })
            }
            value={row.kind}
          >
            <option value="speed">{strings.kindSpeed}</option>
            <option value="time">{strings.kindTime}</option>
          </select>
          <input
            aria-label={t(valueLabel, { n })}
            className={`${INPUT_MONO} min-w-0 flex-1`}
            id={`${base}-value`}
            inputMode="decimal"
            onChange={(event) => onChange({ value: event.target.value })}
            value={row.value}
          />
          <select
            aria-label={t(strings.unitFor, { name: t(valueLabel, { n }) })}
            className={`${SELECT.replace("w-full", "")} w-24 shrink-0`}
            onChange={(event) =>
              onChange(
                row.kind === "speed"
                  ? { speedUnit: event.target.value }
                  : { timeUnit: event.target.value }
              )
            }
            value={row.kind === "speed" ? row.speedUnit : row.timeUnit}
          >
            {unitOptions(row.kind === "speed" ? SPEED : TIME).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {canRemove ? (
        <div className="sm:col-span-2">
          <button className={BUTTON_SMALL} onClick={onRemove} type="button">
            {t(strings.removeLeg, { n })}
          </button>
        </div>
      ) : null}
    </fieldset>
  );
}

export function TripInputs({
  rows,
  onRows,
  units,
  onUnits,
  strings,
  nextId,
}: {
  rows: TripRow[];
  onRows: (rows: TripRow[]) => void;
  units: TripOutputUnits;
  onUnits: (units: TripOutputUnits) => void;
  strings: TripStrings;
  nextId: () => number;
}) {
  const patch = (id: number, change: Partial<TripRow>) =>
    onRows(rows.map((row) => (row.id === id ? { ...row, ...change } : row)));
  return (
    <div className="space-y-4">
      {rows.map((row, index) => (
        <LegEditor
          canRemove={rows.length > 1}
          index={index}
          key={row.id}
          onChange={(change) => patch(row.id, change)}
          onRemove={() => onRows(rows.filter((other) => other.id !== row.id))}
          row={row}
          strings={strings}
        />
      ))}
      {rows.length < MAX_LEGS ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            const last = rows.at(-1);
            onRows([
              ...rows,
              {
                id: nextId(),
                distance: "",
                distanceUnit: last?.distanceUnit ?? "km",
                kind: last?.kind ?? "speed",
                value: "",
                speedUnit: last?.speedUnit ?? "kph",
                timeUnit: last?.timeUnit ?? "h",
              },
            ]);
          }}
          type="button"
        >
          {strings.addLeg}
        </button>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="sdt-out-distance"
          label={strings.showDistanceIn}
          onChange={(distance) => onUnits({ ...units, distance })}
          options={unitOptions(LENGTH)}
          value={units.distance}
        />
        <SelectField
          id="sdt-out-speed"
          label={strings.showSpeedIn}
          onChange={(speed) => onUnits({ ...units, speed })}
          options={unitOptions(SPEED)}
          value={units.speed}
        />
        <SelectField
          id="sdt-out-time"
          label={strings.showTimeIn}
          onChange={(time) => onUnits({ ...units, time })}
          options={unitOptions(TIME)}
          value={units.time}
        />
      </div>
    </div>
  );
}

function legRows(
  legs: readonly Leg[],
  strings: TripStrings,
  units: TripOutputUnits,
  fmt: NumberFormatter,
  sig: number
): string[][] {
  return legs.map((leg, index) => [
    t(strings.columnLeg, { n: index + 1 }),
    formatSig(fmt, fromBase(leg.distance, LENGTH, units.distance), sig),
    formatSig(fmt, fromBase(leg.speed, SPEED, units.speed), sig),
    formatSig(fmt, fromBase(leg.time, TIME, units.time), sig),
  ]);
}

export function TripResults({
  rows,
  units,
  strings,
  fmt,
  sig,
}: {
  rows: readonly TripRow[];
  units: TripOutputUnits;
  strings: TripStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const inputs = readRows(rows);
  if (typeof inputs === "number") {
    return (
      <ErrorNotice>{t(strings.tripUnreadable, { n: inputs + 1 })}</ErrorNotice>
    );
  }
  const result = solveTrip(inputs);
  if (!result.ok) {
    return (
      <ErrorNotice>
        {t(strings.tripErrors[result.error], { n: (result.leg ?? 0) + 1 })}
      </ErrorNotice>
    );
  }
  const distanceSymbol = unitSymbol(LENGTH, units.distance);
  const speedSymbol = unitSymbol(SPEED, units.speed);
  const timeSymbol = unitSymbol(TIME, units.time);
  const avg = formatSig(
    fmt,
    fromBase(result.averageSpeed, SPEED, units.speed),
    sig
  );
  const mean = formatSig(
    fmt,
    fromBase(result.meanOfSpeeds, SPEED, units.speed),
    sig
  );
  const differs =
    Math.abs(result.meanOfSpeeds - result.averageSpeed) >
    0.005 * result.averageSpeed;
  const totalDistance = formatSig(
    fmt,
    fromBase(result.totalDistance, LENGTH, units.distance),
    sig
  );
  const totalTime = formatSig(
    fmt,
    fromBase(result.totalTime, TIME, units.time),
    sig
  );
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.averageSpeed}
          value={`${avg} ${speedSymbol}`}
        />
        <StatCard
          label={strings.totalDistance}
          value={`${totalDistance} ${distanceSymbol}`}
        />
        <StatCard
          label={strings.totalTime}
          value={`${totalTime} ${timeSymbol}`}
        />
      </StatGrid>
      <p className={HINT}>
        {t(strings.hmsLine, { time: hmsText(result.totalTime, strings, fmt) })}
      </p>
      <DataTable
        head={[
          strings.tripHeading,
          `${strings.columnDistance} (${distanceSymbol})`,
          `${strings.columnSpeed} (${speedSymbol})`,
          `${strings.columnTime} (${timeSymbol})`,
        ]}
        rows={[
          ...legRows(result.legs, strings, units, fmt, sig),
          [strings.columnTotal, totalDistance, avg, totalTime],
        ]}
      />
      <Notice>
        {t(strings.tripFormula, {
          distance: `${totalDistance} ${distanceSymbol}`,
          time: `${totalTime} ${timeSymbol}`,
          speed: `${avg} ${speedSymbol}`,
        })}
      </Notice>
      {differs ? (
        <Notice>
          {t(strings.meanNote, {
            mean: `${mean} ${speedSymbol}`,
            average: `${avg} ${speedSymbol}`,
          })}
        </Notice>
      ) : null}
    </>
  );
}
